import { ReactNode, useState } from "react";
import { Box, Card, CardContent, CardHeader, Collapse } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Task, TaskKind } from "../../../app/backendTypes";
import TaskStatus from "./TaskStatus";
import TaskDescription from "./TaskDescription";
import CancelTask from "./CancelTask";
import ExpandMore from "../ExpandMore";
import TaskHistory from "./TaskHistory";
import ApprovalActions from "./ApprovalActions";
import RetryTask from "./RetryTask";

export default function TaskView(props: {
  task: Task;
  callback?: () => void;
  expanded?: boolean;
  close?: ReactNode;
  archived?: boolean;
}) {
  const {
    task,
    expanded: preExpanded = false,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    callback = () => {},
    close,
    archived = false,
  } = props;

  const [expanded, setExpanded] = useState(preExpanded);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const { title, subheader } = deriveTitleAndSubheader(task.kind);

  return (
    <Card sx={{ width: "100%", maxWidth: "sm" }}>
      <CardHeader
        title={title}
        subheader={subheader}
        action={
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              paddingLeft: 2,
            }}
          >
            <TaskStatus task={task} />
            {close !== undefined && close}
          </Box>
        }
      />
      <CardContent>
        <TaskDescription task={task} />
        <Collapse in={expanded} timeout="auto">
          <TaskHistory task={task} open={expanded} />
        </Collapse>
        <br />
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
          {!archived && (
            <>
              <ApprovalActions task={task} callback={callback} />
              <CancelTask task={task} callback={callback} />
              <RetryTask task={task} callback={callback} />
            </>
          )}
          <ExpandMore expand={expanded} onClick={handleExpandClick}>
            <ExpandMoreIcon />
          </ExpandMore>
        </Box>
      </CardContent>
    </Card>
  );
}

function deriveTitleAndSubheader(kind: TaskKind) {
  let title;
  let subheader;

  switch (kind.kind) {
    case "ToolAccess": {
      title = "Tool Access";
      subheader = kind.inner.tool;
      break;
    }

    case "CatalogAccess": {
      title = "Catalog Access";
      subheader = kind.inner.catalog_name;
      break;
    }

    case "JiraProjectAccess": {
      title = "Jira Project Access";
      subheader = kind.inner.project_name;
      break;
    }

    case "BlackDuckProjectGroupAccess": {
      title = "Black Duck Project Group Access";
      subheader = kind.inner.project_group_name;
      break;
    }

    case "PolarisGroupAccess": {
      title = "Polaris Group Access";
      subheader = kind.inner.group_name;
      break;
    }

    case "TableauSiteRoleChange": {
      title = "Tableau Site Role Change";
      subheader = kind.inner.role;
      break;
    }

    case "TableauProjectRoleChange": {
      title = "Tableau Project Role Change";
      subheader = `${kind.inner.project_name} - ${kind.inner.role}`;
      break;
    }

    case "DatasetAccess": {
      title = "Dataset Access";
      subheader = kind.inner.collection_name;
      break;
    }

    case "EmployeeOffboarding": {
      title = "Employee Offboarding";
      subheader = `${kind.inner.employee.name || kind.inner.employee.id} - ${
        kind.inner.employee.email
      }`;
      break;
    }

    case "Support": {
      title = "Support";
      subheader = `${kind.inner.details.routing.jiraRequestType} for ${
        kind.inner.resource.kind === "Tool"
          ? kind.inner.resource.inner.id
          : kind.inner.resource.inner.name
      }`;
      break;
    }

    case "LoadContracts": {
      title = "Load Contracts";
      break;
    }

    case "RetryEmployeeOffboardings": {
      title = "Retry Employee Offboardings";
      break;
    }

    case "SyncUsers": {
      title = "Synchronize Users";
      break;
    }

    case "CarolineIndex": {
      title = "Caroline Index";
      break;
    }

    default: {
      title = "Unknown";
    }
  }

  return { title, subheader };
}
